import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../../Shared/Formatters';

/**
 * Pivoted bucket × series grid. Plain HTML table — readable, dense,
 * no extra deps. (ag-grid is overkill for 6 columns.)
 *
 * Final row shows totals per series so the eye can sanity-check the
 * KPI strip against the chart.
 */
export default function ResultsTable({ seriesData, metric, alignPeriods, palette }) {
    const { t } = useTranslation();
    const fld = metricFieldKey(metric);
    const fmt = metric === 'transactions' ? (v) => Math.round(v).toLocaleString() : formatMoney;

    const { rows, totals } = useMemo(() => buildRows(seriesData, fld, alignPeriods), [seriesData, fld, alignPeriods]);

    if (rows.length === 0) return null;

    return (
        <div style={wrap}>
            <table style={table}>
                <thead>
                    <tr>
                        <th style={th}>{alignPeriods ? t('Bucket') : t('Date')}</th>
                        {seriesData.map((s, i) => (
                            <th key={i} style={th}>
                                <span style={{ ...swatch, background: palette[i] }} />
                                {labelOf(s)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, ri) => (
                        <tr key={ri}>
                            <td style={td}>
                                {alignPeriods ? `#${ri + 1}` : r.bucket}
                                {alignPeriods && (
                                    <span style={{ color: 'var(--v3-text-muted)', fontSize: 10, marginLeft: 6 }}>
                                        {r.originals.filter(Boolean).join(' / ') || ''}
                                    </span>
                                )}
                            </td>
                            {r.values.map((v, vi) => (
                                <td key={vi} style={tdNum}>{v == null ? '—' : fmt(v)}</td>
                            ))}
                        </tr>
                    ))}
                    <tr>
                        <td style={{ ...td, fontWeight: 600 }}>{t('Total')}</td>
                        {totals.map((t, ti) => (
                            <td key={ti} style={{ ...tdNum, fontWeight: 600 }}>{fmt(t)}</td>
                        ))}
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

function metricFieldKey(metric) {
    if (metric === 'transactions') return 'trans_count';
    if (metric === 'revenue') return 'revenue_share_amount';
    return 'sales';
}

function labelOf(s) { return s.label || `${s.type}: ${s.id}`; }

function buildRows(seriesData, fld, alignPeriods) {
    if (!seriesData || seriesData.length === 0) return { rows: [], totals: [] };

    if (alignPeriods) {
        const max = Math.max(...seriesData.map(s => (s.series || []).length));
        const rows = [];
        for (let i = 0; i < max; i++) {
            const values = seriesData.map(s => {
                const r = (s.series || [])[i];
                return r ? (r[fld] || 0) : null;
            });
            const originals = seriesData.map(s => {
                const r = (s.series || [])[i];
                return r ? String(r._id) : '';
            });
            rows.push({ bucket: `#${i + 1}`, values, originals });
        }
        const totals = seriesData.map(s => (s.series || []).reduce((a, r) => a + (r[fld] || 0), 0));
        return { rows, totals };
    }

    const set = new Set();
    seriesData.forEach(s => (s.series || []).forEach(r => set.add(String(r._id))));
    const buckets = [...set].sort();
    const rows = buckets.map(b => {
        const values = seriesData.map(s => {
            const r = (s.series || []).find(x => String(x._id) === b);
            return r ? (r[fld] || 0) : null;
        });
        return { bucket: b, values, originals: [] };
    });
    const totals = seriesData.map(s => (s.series || []).reduce((a, r) => a + (r[fld] || 0), 0));
    return { rows, totals };
}

const wrap = {
    marginTop: 16,
    overflowX: 'auto',
    border: '1px solid var(--v3-glass-border)',
    borderRadius: 10,
    background: 'var(--v3-bg-surface)',
};
const table = { width: '100%', borderCollapse: 'collapse', fontSize: 12 };
const th = {
    textAlign: 'left',
    padding: '10px 12px',
    color: 'var(--v3-text-muted)',
    borderBottom: '1px solid var(--v3-glass-border)',
    background: 'var(--v3-bg-elevated, transparent)',
    whiteSpace: 'nowrap',
};
const td = {
    padding: '8px 12px',
    color: 'var(--v3-text-primary)',
    borderBottom: '1px solid var(--v3-glass-border)',
    whiteSpace: 'nowrap',
};
const tdNum = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
const swatch = { display: 'inline-block', width: 8, height: 8, borderRadius: 2, marginRight: 6, verticalAlign: 'middle' };
